// Self-serve "Change password" dialog — sidebar footer, all roles.
// Calls the existing POST /api/domain/change-password endpoint.
import React, { useState } from 'react';
import axios from 'axios';
import { KeyRound, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../../ui/dialog';
import { Button } from '../../ui/button';
import { PasswordStrengthMeter } from '../PasswordStrengthMeter';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const ChangePasswordButton = ({ variant = 'sidebar' }) => {
  const [open, setOpen] = useState(false);
  const [currentPw, setCurrentPw] = useState('');
  const [newPw, setNewPw] = useState('');
  const [confirmPw, setConfirmPw] = useState('');
  const [saving, setSaving] = useState(false);

  const reset = () => { setCurrentPw(''); setNewPw(''); setConfirmPw(''); };

  const submit = async (e) => {
    e.preventDefault();
    if (newPw.length < 8) { toast.error('New password must be at least 8 characters'); return; }
    if (newPw !== confirmPw) { toast.error('New passwords do not match'); return; }
    setSaving(true);
    try {
      await axios.post(`${API}/domain/change-password`, {
        current_password: currentPw, new_password: newPw,
      });
      toast.success('Password changed');
      reset();
      setOpen(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not change password');
    } finally {
      setSaving(false);
    }
  };

  const inputCls = 'w-full text-sm border border-slate-200 dark:border-white/10 rounded-lg px-3 py-2 bg-transparent focus:outline-none focus:ring-2 focus:ring-teal-500/40';

  return (
    <>
      {variant === 'page' ? (
        <Button
          variant="outline"
          size="sm"
          className="text-slate-600"
          onClick={() => setOpen(true)}
          data-testid="change-password-open-btn"
        >
          <KeyRound className="w-4 h-4 mr-1.5" />
          Change password
        </Button>
      ) : (
        <Button
          variant="ghost"
          className="w-full justify-start text-slate-600 hover:text-slate-900 hover:bg-slate-100 mb-1"
          onClick={() => setOpen(true)}
          data-testid="change-password-open-btn"
        >
          <KeyRound className="w-4 h-4 mr-2" />
          Change password
        </Button>
      )}
      <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) reset(); }}>
        <DialogContent className="max-w-sm" data-testid="change-password-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <KeyRound className="w-4 h-4 text-teal-600" /> Change password
            </DialogTitle>
            <DialogDescription>
              Enter your current password (or the temporary one you were given), then choose a new one — minimum 8 characters.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={submit} className="space-y-3">
            <div>
              <label className="text-[11px] font-bold uppercase tracking-wide opacity-60">Current password</label>
              <input type="password" value={currentPw} onChange={(e) => setCurrentPw(e.target.value)}
                autoComplete="current-password" required className={inputCls}
                data-testid="change-password-current-input" />
            </div>
            <div>
              <label className="text-[11px] font-bold uppercase tracking-wide opacity-60">New password</label>
              <input type="password" value={newPw} onChange={(e) => setNewPw(e.target.value)}
                autoComplete="new-password" required minLength={8} className={inputCls}
                data-testid="change-password-new-input" />
              <PasswordStrengthMeter password={newPw} />
            </div>
            <div>
              <label className="text-[11px] font-bold uppercase tracking-wide opacity-60">Confirm new password</label>
              <input type="password" value={confirmPw} onChange={(e) => setConfirmPw(e.target.value)}
                autoComplete="new-password" required minLength={8} className={inputCls}
                data-testid="change-password-confirm-input" />
            </div>
            <button type="submit" disabled={saving || !currentPw || !newPw || !confirmPw}
              className="w-full inline-flex items-center justify-center gap-2 rounded-lg bg-teal-600 hover:bg-teal-500 disabled:opacity-40 text-white text-sm font-bold py-2.5 transition-colors"
              data-testid="change-password-submit-btn">
              {saving && <Loader2 className="w-4 h-4 animate-spin" />}
              {saving ? 'Saving…' : 'Update password'}
            </button>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
};
